import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ModalCountrySelector } from './ModalCountrySelector';
import { updateUserProfile } from '@/lib/user-service';
import { useAuth } from '@/contexts/AuthContext';
import { Sparkles } from 'lucide-react';

interface ProfileCompletionModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

export const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({
  isOpen,
  onComplete,
}) => {
  const { user } = useAuth();
  const logo = '/Cheflab.png';
  const leftGradient = 'bg-gradient-to-br from-white via-yellow-50 to-yellow-100';
  const leftTextSubtle = 'text-gray-700';
  const modalBg = 'bg-white/95';
  const borderColor = 'border-gray-200';
  const formLabel = 'text-gray-700';
  const inputText = 'text-gray-700 placeholder:text-gray-400 bg-white/80';

  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setError('User not found');
      return;
    }

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!country) {
      setError('Please select your country');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await updateUserProfile(user.id, {
        name: name.trim(),
        country: country,
      });
      onComplete();
    } catch {
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem('skippedProfileCompletion', 'true');
    onComplete();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-backdrop-in"
        onClick={handleSkip}
      />
      {/* Modal card */}
      <div className={`relative z-10 ${modalBg} glass-ios-strong rounded-t-3xl sm:rounded-3xl shadow-2xl border ${borderColor} overflow-hidden max-w-sm sm:max-w-md md:max-w-4xl w-full animate-slide-up-mobile sm:animate-none pb-[max(env(safe-area-inset-bottom),0px)] max-h-[100dvh] sm:max-h-[85vh]`}>
        <div className="flex flex-col md:flex-row overflow-y-auto max-h-[100dvh] sm:max-h-[85vh]">
          {/* Left side - Welcome content */}
          <div className={`${leftGradient} p-8 flex flex-col justify-center relative min-h-[280px] md:min-h-[420px] md:w-1/2 items-center pt-12 md:pt-8`}>
            <div className="relative z-10 text-center w-full flex flex-col items-center">
              <img
                src={logo}
                alt="Chef Lab logo"
                className="h-16 w-auto object-contain mx-auto mb-4 drop-shadow-xl"
              />
              {/* New: Welcome + profile completion message under logo, above coins */}
              <p className={`${leftTextSubtle} text-base md:text-lg leading-relaxed max-w-xs mx-auto mb-2`}>
                <span className="font-bold text-yellow-600">Welcome!</span> Complete your profile and get 100 free ChefCoins.
              </p>
              {/* ChefCoin image - larger, no border/circle, just shadow */}
              <img
                src="/chefcoin.png"
                alt="ChefCoin"
                className="h-22 w-22 max-h-24 max-w-24 mb-2 animate-in fade-in-0 slide-in-from-top-2 duration-500"
                style={{ filter: 'drop-shadow(0 4px 24px #fbbf24aa)', height: '86px', width: '86px' }}
              />
              <p className={`${leftTextSubtle} text-base md:text-lg leading-relaxed max-w-xs mx-auto mt-2`}>
                Use your coins to for ChefLab's premium AI features including the Recipe Studio!
                <Sparkles className="inline align-middle mb-0.5 ml-1 text-yellow-400 animate-bounce-slow drop-shadow" aria-label="AI Studio" />
              </p>
            </div>
          </div>

          {/* Right side - Form */}
          <div className="p-6 md:p-8 w-full md:w-1/2">
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              <div>
                <Label htmlFor="name" className={`${formLabel} font-medium mb-2 block`}>
                  First Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="e.g. Eric"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className={`w-full h-12 ${inputText} border-0 rounded-xl focus:ring-2 focus:ring-purple-400 focus:bg-white transition-all`}
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              <div>
                <Label htmlFor="country" className={`${formLabel} font-medium mb-2 block`}>
                  Country
                </Label>
                <ModalCountrySelector
                  value={country}
                  onValueChange={setCountry}
                  placeholder="Select your country"
                  className={`w-full h-12 ${inputText} border-0 rounded-xl focus:ring-2 focus:ring-purple-400 focus:bg-white transition-all`}
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div className="text-sm text-red-700 bg-red-50 p-2 rounded">
                  {error}
                </div>
              )}

              <div className="flex flex-row items-center gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isLoading}
                  onClick={handleSkip}
                  className="flex-1 h-12 rounded-xl"
                >
                  Skip for Now
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 h-12 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-xl border-0 shadow-lg transition-all"
                >
                  {isLoading ? 'Saving...' : 'Complete Profile'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionModal;